import { StrapiCollectionType, StrapiType } from "@/src/type/strapi";

import { STRAPI_POPULATE_OPTIONS } from "./StrapiPopulate";
import { get, StrapiApiServiceType } from "./StrapiService";

async function getProperty(slug: string, locale?: string, populate?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.Property>>(
    "properties",
    locale ?? "en",
    `filters[slug][$eq]=${slug}`,
    undefined,
    populate
  );
  return getType<StrapiCollectionType.Property>(response);
}

async function getPropertiesBySlugs(slugs: string[], locale?: string) {
  let i = 0;
  const filters = [];
  for (const slug of slugs) {
    filters.push(`filters[slug][$in][${i}]=${slug}`);
    i++;
  }

  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.Property>>("properties", locale ?? "en", filters.join("&"));
  return getTypeArray<StrapiCollectionType.Property>(response);
}

async function getProperties(locale?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.Property>>("properties", locale ?? "en");
  return getTypeArray<StrapiCollectionType.Property>(response);
}

async function getPropertyContentPage(slug: string, locale?: string, populate?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.PropertyContentPage>>(
    "property-content-pages",
    locale ?? "en",
    `filters[property][slug][$eq]=${slug}`,
    undefined,
    populate
  );
  return getType<StrapiCollectionType.PropertyContentPage>(response);
}
async function getPropertyContent(slug: string, locale?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.PropertyContent>>(
    "property-contents",
    locale ?? "en",
    `filters[property][slug][$eq]=${slug}`
  );
  return getTypeArray<StrapiCollectionType.PropertyContent>(response);
}

async function getPropertyContentItemsBySlug(slugs: string[], locale?: string) {
  let i = 0;
  const filters = [];
  for (const slug of slugs) {
    filters.push(`filters[slug][$in][${i}]=${slug}`);
    i++;
  }

  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.PropertyContentItem>>(
    "property-content-items",
    locale ?? "en",
    filters.join("&")
  );
  return getTypeArray<StrapiCollectionType.PropertyContentItem>(response);
}

async function getDyanmicPages(domain: string, locale?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.DynamicPage>>(
    "dynamic-pages",
    locale ?? "en",
    undefined,
    `filters[domains][hostname][$eq]=${domain}`
  );
  return getTypeArray<StrapiCollectionType.DynamicPage>(response);
}

async function getDyanmicPagesGetSlugs(domain: string, locale?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.DynamicPage>>(
    "dynamic-pages",
    locale ?? "en",
    undefined,
    `dynamic-pages?locale=${locale}&filters[domains][hostname][$eq]=${domain}&populate=property`
  );
  return getTypeArray<StrapiCollectionType.DynamicPage>(response);
}

async function getDyanmicPage(domain: string, slug: string, locale?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.DynamicPage>>(
    "dynamic-pages",
    locale ?? "en",
    `filters[slug][$eq]=${slug}&filters[domains][hostname][$eq]=${domain}&filters[property][$null]=true`
  );
  return getType<StrapiCollectionType.DynamicPage>(response);
}

async function getPropertyDyanmicPage(domain: string, propertySlug: string, slug: string, locale?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.DynamicPage>>(
    "dynamic-pages",
    locale ?? "en",
    `filters[slug][$eq]=${slug}&filters[domains][hostname][$eq]=${domain}&filters[property][slug][$eq]=${propertySlug}`
  );
  return getType<StrapiCollectionType.DynamicPage>(response);
}

async function getDyanmicPageById(id: number) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.DynamicPage>>(
    undefined,
    undefined,
    undefined,
    `dynamic-pages?filters[id][$eq]=${id}&populate=localizations`
  );
  return getType<StrapiCollectionType.DynamicPage>(response);
}

async function getDyanmicPagesBySectionId(id: number) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.DynamicPage>>(
    undefined,
    undefined,
    undefined,
    `dynamic-pages?filters[dynamic_page_sections][id][$eq]=${id}&populate=localizations`
  );
  return getTypeArray<StrapiCollectionType.DynamicPage>(response);
}

async function getHelpContentPagesByPage(page?: StrapiCollectionType.HelpContentPageOption, locale?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.HelpContentPage>>(
    "help-content-pages",
    locale ?? "en",
    `filters[page][$eq]=${page}`
  );
  return getType<StrapiCollectionType.HelpContentPage>(response);
}

async function getHelpContentBySlug(slug?: string, locale?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.HelpContent>>(
    "help-contents",
    locale ?? "en",
    `filters[slug][$eq]=${slug}`
  );
  return getType<StrapiCollectionType.HelpContent>(response);
}

async function getPackagesByUnitTypeId(propertySlug: string, locale?: string, populate?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.Package>>(
    "packages",
    locale ?? "en",
    `filters[properties][slug][$eq]=${propertySlug}`,
    undefined,
    populate ?? STRAPI_POPULATE_OPTIONS.PACKAGES
  );
  return getTypeArray<StrapiCollectionType.Package>(response);
}

async function getUnitTypesByPropertySlug(slug: string, locale?: string, populate?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.PropertyUnitType>>(
    "property-unit-types",
    locale ?? "en",
    `filters[property][slug][$eq]=${slug}`,
    undefined,
    populate ?? STRAPI_POPULATE_OPTIONS.UNIT_TYPES
  );
  return getTypeArray<StrapiCollectionType.PropertyUnitType>(response);
}

async function getPropertyFloorplansByPropertySlug(slug: string, locale?: string, populate?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.PropertyFloorplan>>(
    "property-floorplans",
    locale ?? "en",
    `filters[property][slug][$eq]=${slug}&filters[status][$eq]=available`,
    undefined,
    populate ?? STRAPI_POPULATE_OPTIONS.UNIT_TYPES
  );
  return getTypeArray<StrapiCollectionType.PropertyFloorplan>(response);
}

async function getNavigationByPageSlug(slug: string, locale?: string, domain?: string, populate?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.Navigation>>(
    "navigations",
    locale ?? "en",
    `filters[pages][slug][$eq]=${slug}&filters[domains][hostname][$eq]=${domain}&filters[pages][property][$null]=true`,
    undefined,
    populate ?? STRAPI_POPULATE_OPTIONS.NAVIGATION
  );
  return getType<StrapiCollectionType.Navigation>(response);
}

async function getPropertyNavigationByPageSlug(slug: string, propertySlug: string, locale?: string, domain?: string, populate?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.Navigation>>(
    "navigations",
    locale ?? "en",
    `filters[pages][slug][$eq]=${slug}&filters[domains][hostname][$eq]=${domain}&filters[pages][property][slug][$eq]=${propertySlug}`,
    undefined,
    populate ?? STRAPI_POPULATE_OPTIONS.NAVIGATION
  );
  return getType<StrapiCollectionType.Navigation>(response);
}

async function getNavigationById(id: number) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.Navigation>>(
    undefined,
    undefined,
    undefined,
    `navigations?filters[id][$eq]=${id}&populate=pages&${STRAPI_POPULATE_OPTIONS.NAVIGATION}`
  );
  return getType<StrapiCollectionType.Navigation>(response);
}

async function getMarketingSources(locale?: string) {
  const response = await get<StrapiType.StrapiCollectionWrapper<StrapiCollectionType.MarketingSource>>(
    "marketing-sources",
    locale ?? "en",
    undefined,
    undefined,
    STRAPI_POPULATE_OPTIONS.MARKETING_SOURCES
  );
  return getTypeArray<StrapiCollectionType.MarketingSource>(response);
}

function getType<T>(response: StrapiApiServiceType<StrapiType.StrapiCollectionWrapper<T>>): T | undefined {
  const data = response?.response?.data?.[0]?.attributes;
  if (data) {
    //@ts-ignore
    data["id"] = response?.response?.data?.[0]?.id;
  }
  return data;
}

function getTypeArray<T>(response: StrapiApiServiceType<StrapiType.StrapiCollectionWrapper<T>>): T[] | undefined {
  const arr: T[] = [];
  for (const opt of response?.response?.data ?? []) {
    if (opt?.id && opt?.attributes) {
      //@ts-ignore
      opt.attributes.id = opt.id;
      arr.push(opt.attributes);
    }
  }
  return arr;
}

export default {
  getProperty,
  getPropertiesBySlugs,
  getProperties,
  getPropertyContent,
  getPropertyContentItemsBySlug,
  getDyanmicPages,
  getPropertyDyanmicPage,
  getDyanmicPagesGetSlugs,
  getDyanmicPage,
  getHelpContentPagesByPage,
  getHelpContentBySlug,
  getPackagesByUnitTypeId,
  getUnitTypesByPropertySlug,
  getDyanmicPageById,
  getDyanmicPagesBySectionId,
  getNavigationByPageSlug,
  getPropertyNavigationByPageSlug,
  getNavigationById,
  getPropertyContentPage,
  getPropertyFloorplansByPropertySlug,
  getMarketingSources,
};
